<script lang="ts">
  import { onDestroy } from 'svelte';
  import { Link } from 'svelte-routing';
  import { api } from '../api/client';
  import type { Person, SosAlert } from '../api/types';
  import Modal from './Modal.svelte';
  import { integer, t } from '../i18n/index.ts';

  // SosButton sends the member's SOS from the web (spec 004): holding
  // it for 2 seconds starts a 5-second countdown with Cancel and Send now
  // (004 FR-010), and the SOS carries the browser's location when the member
  // allows it.
  let { onsent }: { onsent: (a: SosAlert) => void } = $props();

  const HOLD_MS = 2000;
  const COUNT_S = 5;

  // held is how far through the hold the press is, 0 to 1.
  let held = $state(0);
  let counting = $state(false);
  let left = $state(COUNT_S);
  let sending = $state(false);
  let noContacts = $state(false);
  // shown is whether the map offers SOS: not while the member has nobody on
  // their SOS list, but while the list cannot be read, including before it
  // answers (004 FR-012).
  let shown = $state(true);
  api
    .get<Person[]>('/me/sos-contacts')
    .then((contacts) => (shown = contacts.length > 0))
    .catch(() => {});
  let error = $state('');
  let holdStart = 0;
  let holdFrame = 0;
  let countTimer: ReturnType<typeof setInterval> | null = null;
  // FIX_MS bounds the wait for the browser's position. The browser's own
  // timeout leaves out the time its permission prompt is open, so the wait
  // is capped here too.
  const FIX_MS = 10_000;
  // fix is the browser's position, asked for when the countdown starts, or
  // null when it has none within FIX_MS.
  let fix: Promise<GeolocationPosition | null> = Promise.resolve(null);

  onDestroy(() => {
    cancelAnimationFrame(holdFrame);
    if (countTimer) clearInterval(countTimer);
  });

  function press() {
    if (counting || sending) return;
    holdStart = performance.now();
    const step = () => {
      held = Math.min(1, (performance.now() - holdStart) / HOLD_MS);
      if (held >= 1) {
        held = 0;
        startCountdown();
        return;
      }
      holdFrame = requestAnimationFrame(step);
    };
    holdFrame = requestAnimationFrame(step);
  }

  function release() {
    cancelAnimationFrame(holdFrame);
    held = 0;
  }

  function startCountdown() {
    error = '';
    // The count starts at once; the list loads alongside. Nobody to send it
    // to stops the count and asks for people first (004 FR-010, 004 FR-012).
    api
      .get<Person[]>('/me/sos-contacts')
      .then((contacts) => {
        if (contacts.length === 0 && counting) {
          cancel();
          noContacts = true;
        }
      })
      .catch(() => {});
    fix = new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      setTimeout(() => resolve(null), FIX_MS);
      navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), {
        enableHighAccuracy: true,
        timeout: FIX_MS,
        maximumAge: 30_000,
      });
    });
    counting = true;
    left = COUNT_S;
    countTimer = setInterval(() => {
      left--;
      if (left <= 0) send();
    }, 1000);
  }

  function cancel() {
    if (countTimer) clearInterval(countTimer);
    countTimer = null;
    counting = false;
  }

  async function send() {
    if (sending) return;
    if (countTimer) clearInterval(countTimer);
    countTimer = null;
    sending = true;
    const body: Record<string, unknown> = { source: 'web', sent_at: new Date().toISOString() };
    // The SOS waits for the position asked for at the countdown's start, at
    // most FIX_MS from then; without one the server uses the member's latest
    // report (004 US7).
    const at = await fix;
    if (at) {
      body.lat = at.coords.latitude;
      body.lon = at.coords.longitude;
      body.accuracy_m = at.coords.accuracy;
      body.located_at = new Date(at.timestamp).toISOString();
    }
    try {
      const a = await api.put<SosAlert>(`/me/sos/${crypto.randomUUID()}`, body);
      counting = false;
      onsent(a);
    } catch (e: any) {
      error = e.message;
    } finally {
      sending = false;
    }
  }

  function key(e: KeyboardEvent, down: boolean) {
    if (e.key !== ' ' && e.key !== 'Enter') return;
    e.preventDefault();
    if (down && !e.repeat) press();
    if (!down) release();
  }
</script>

{#if shown}
  <button
    class="sos-button"
    style="--held: {held * 360}deg;"
    title={t('componentsSosHoldTitle')}
    aria-label={t('componentsSosHoldLabel')}
    onpointerdown={press}
    onpointerup={release}
    onpointerleave={release}
    onpointercancel={release}
    onkeydown={(e) => key(e, true)}
    onkeyup={(e) => key(e, false)}
    oncontextmenu={(e) => e.preventDefault()}
  >
    {t('componentsSosButton')}
  </button>
{/if}

{#if counting}
  <Modal title={t('componentsSosSending')} onclose={cancel}>
    <p class="text-5xl font-bold text-center my-4" style="color: var(--theme-error);">{integer(left)}</p>
    <p class="text-sm text-center mb-4">{sending ? t('componentsSosFinding') : t('componentsSosCountdown')}</p>
    {#if error}<p class="text-sm mb-3" style="color: var(--theme-error);">{error}</p>{/if}
    <div class="flex justify-center gap-3">
      <button class="btn btn-secondary" onclick={cancel} disabled={sending}>{t('appCancel')}</button>
      <button class="btn btn-danger" onclick={send} disabled={sending}>{t('componentsSosSendNow')}</button>
    </div>
  </Modal>
{/if}

{#if noContacts}
  <Modal title={t('componentsSosNoContactsTitle')} onclose={() => (noContacts = false)}>
    <p class="text-sm mb-4">{t('componentsSosNoContacts')}</p>
    <Link to="/settings/safety" class="btn btn-primary inline-block" onclick={() => (noContacts = false)}>{t('componentsSosChoosePeople')}</Link>
  </Modal>
{/if}

<style>
  .sos-button {
    width: 3rem;
    height: 3rem;
    border-radius: 9999px;
    font-weight: 800;
    letter-spacing: 0.05em;
    color: var(--theme-base);
    background:
      conic-gradient(var(--theme-text) var(--held), transparent 0) border-box,
      var(--theme-error) padding-box;
    border: 3px solid transparent;
    font-size: 0.875rem;
    box-shadow: var(--theme-shadow-md);
    touch-action: none;
    user-select: none;
  }
</style>
